<template>
	<div>
		<LoadingVue v-if="isAccoutCheck" />
		<!-- タイトル部分 -->
		<div class="data-update-title">
			<h2>データ更新画面</h2>
			<router-link to="/">AIチャットボットに戻る</router-link>
		</div>

		<!-- ローディングスピナー表示 -->
		<div v-if="isLoading" class="loading-overlay">
			<div class="loading-spinner"></div>
		</div>

		<!-- 成功メッセージ -->
		<div v-if="successMessage" class="success-message">
			<span class="close-button" @click="successMessage = ''">&times;</span>
			{{ successMessage }}
		</div>

		<!-- 失敗メッセージ -->
		<div v-if="errorMessage" class="error-message">
			<span class="close-button" @click="errorMessage = ''">&times;</span>
			{{ errorMessage }}
		</div>

		<!-- コンテンツ部分 -->
		<div class="data-update-content">
			<!-- データ更新に関する操作 -->
			<table>
				<thead>
					<tr>
						<th>項目</th>
						<th>更新日</th>
						<th>蓄積ファイル</th>
						<th>更新者</th>
						<th>フォーマット</th>
					</tr>
				</thead>
				<tbody>
					<!-- アイテムリストの表示 -->
					<tr v-for="item in items" :key="item.name">
						<td @click="openUploadModal(item.name)" style="cursor: pointer; color: blue; text-decoration: underline">
							{{ item.name }}
						</td>
						<td>{{ item.date }}</td>
						<td>
							<ul>
								<li v-for="(file, index) in item.files" :key="index">
									<a @click.prevent="handleFileDownload(file.link)" style="cursor: pointer; color: blue; text-decoration: underline">
										{{ file.name }}
									</a>
								</li>
							</ul>
						</td>
						<td>{{ item.user }}</td>
						<td>
							<div style="display: flex; align-items: center; justify-content: center">
								<span style="cursor: pointer; color: blue; text-decoration: underline" @click="handleFileDownload(item.template)">
									ダウンロード
								</span>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<!-- アップロードモーダル -->
			<UploadModal
				v-if="isModalVisible"
				:title="modalTitle"
				:houseId="house_id"
				:userName="userName"
				@close="closeUploadModal"
				@upload-success="handleUploadSuccess"
				@db-upload-success="handleDBSuccess"
				@db-upload-fail="handleDBFail"
				@upload-fail="handleUploadFail"
			/>
		</div>
	</div>
</template>

<script>
import { msalInstance } from "@/msalConfig";
import { useBlobStore } from "@/stores/blobStore";
import { useAuthStore } from "@/stores/authStore";
import UploadModal from "@/components/UploadModal.vue";
import { downloadFile } from "@/utils/fileDownload"; // ダウンロード処理をインポート
import LoadingVue from "@/components/LoadingVue.vue";
import api from "@/utils/api";

export default {
	name: "DataUpdate",
	components: {
		UploadModal,
		LoadingVue,
	},

	data() {
		return {
			isModalVisible: false, // モーダルの表示状態
			modalTitle: "", // モーダルのタイトル
			successMessage: "", // 成功メッセージ
			errorMessage: "", // エラーメッセージ
			// 初期値としての items 配列
			initialItems: [
				{ name: "指導記録", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "guidance_record" },
				{ name: "養液調査", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "nutrient_solution" },
				{ name: "糖度", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "brix_state" },
				{ name: "給液管理", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "liquid_supply_management" },
				{ name: "果実・収量サイズ", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "gross_harvest" },
				{ name: "生育調査", date: "", files: [], user: "", icon: "ダウンロード", template: "", path: "growth_data" },
			],
			items: [], // items 配列
			templateDir: "template",
			isLoading: false, // ローディング状態
			isAccoutCheck: true, // 認証チェック
		};
	},
	mounted() {
		this.checkAccount(); // アカウント情報のチェック
	},

	methods: {
		resetItems() {
			// items を初期値にリセット
			this.items = JSON.parse(JSON.stringify(this.initialItems));
		},

		async checkAccount() {
			this.resetItems(); // items を初期化
			const blobStore = useBlobStore(); // ストアのインスタンスを取得

			// MSALの初期化とサインインチェック
			await msalInstance.initialize();
			const accounts = msalInstance.getAllAccounts();
			const authStore = useAuthStore();

			if (accounts.length > 0) {
				// 一番目のアカウント情報を取得
				this.userName = accounts[0].name;

				// App.vue の watch を発火させるため認証状態をストアに反映
				try {
					authStore.isAuthenticated = true;
					authStore.userName = accounts[0]?.name ?? "";
					authStore.userId = accounts[0]?.homeAccountId ?? "";
					// 共有したいならトークンも
					authStore.apiAccessToken = this.accessToken;
				} catch (e) {
					console.warn("authStore update failed:", e);
				}

				this.isLoading = true; // ローディングを開始

				// fetchFileListとfetchUploadHistoryを並列実行
				try {
					await this.getHouseId();
					// console.log(this.house_id);
					await Promise.all([
						this.fetchFileList(blobStore), // ファイル一覧を取得
						this.fetchUploadHistory(blobStore), // アップロード履歴を取得
					]);
				} catch (error) {
					console.error("Error in fetching data:", error); // エラーハンドリング
				} finally {
					this.isLoading = false; // ローディング終了
				}
				this.isAccoutCheck = false;
			} else {
				this.errorMessage = "ユーザーがサインインしていません。"; // エラーメッセージ設定
				authStore.isAuthenticated = false;
				window.location.href = window.location.origin;
			}
		},

		// 圃場IDを取得
		async getHouseId() {
			try {
				const response = await api.get("", {
					params: {
						action: "GetAccountInfo",
					},
				});
				this.house_id = response.data.house_id; // 圃場IDを格納
			} catch (error) {
				this.errorMessage = "圃場IDの取得に失敗しました。"; // エラーメッセージ設定
				console.error("Error fetching house_id:", error); // エラーをコンソールに表示
			}
		},

		// BlobStorageからファイル一覧を取得
		async fetchFileList(blobStore) {
			try {
				// const directories = [...this.items.map((dir) => dir.path), this.templateDir].join(",");

				// 各ディレクトリのファイルをAPIから取得
				const response = await api.get("", {
					params: {
						action: "GetListBlobs",
						house_id: this.house_id,
					},
				});

				// レスポンスからファイルデータを取得
				const blobs = response.data.blobs;
				blobStore.setItems(blobs); // ストアにファイル一覧を保存
				// console.log("ファイル一覧：", blobs);
				// ディレクトリごとにファイル情報を整理
				for (const [dirPath, files] of Object.entries(blobs)) {
					// ファイル名ごとにグループ化し、最新の日付を持つファイルだけを選択
					const latestFiles = files.reduce((acc, file) => {
						const fileName = file.split("/").pop(); // ファイル名抽出
						const timestamp = file.split("/")[2]; // パスの中から日時部分を抽出（日時は2番目の部分にあると仮定）

						// もしまだファイルが登録されていない場合、または最新の日付が新しい場合は更新
						if (!acc[fileName] || new Date(timestamp) > new Date(acc[fileName].timestamp)) {
							acc[fileName] = {
								link: `${process.env.VUE_APP_BLOB_BASE_URL}${file}`, // Blobのリンク作成
								timestamp: timestamp, // 最新の日時を保存
							};
						}

						return acc;
					}, {});
					// latestFilesのオブジェクトから最終的なfileItemsの配列を作成
					const fileItems = Object.entries(latestFiles).map(([fileName, { link }]) => ({
						name: fileName,
						link: link,
					}));
					// dirPathがtemplateの場合、items内の対応するオブジェクトにテンプレートを追加
					if (dirPath === "template") {
						fileItems.forEach((fileItem) => {
							const matchingItem = this.items.find((item) => fileItem.link.includes(item.path)); // マッチングのためpathを取得
							if (matchingItem) {
								matchingItem.template = fileItem.link; // templateとして追加
							}
						});
					}
					// items内のpathがdirPathと一致するオブジェクトを探す
					const itemToUpdate = this.items.find((item) => item.path === dirPath);
					if (itemToUpdate) {
						if (fileItems) {
							// 一致した場合、filesを更新
							itemToUpdate.files = fileItems;
						}
					}
				}
				// console.log(this.items);
			} catch (error) {
				this.errorMessage = "ファイル一覧の取得に失敗しました。"; // エラーメッセージ設定
				console.error("Error fetching file list:", error); // エラーをコンソールに表示
			}
		},

		// CosmosDBからアップロード履歴を取得
		async fetchUploadHistory(blobStore) {
			try {
				await new Promise((resolve) => setTimeout(resolve, 2000));

				const { data } = await api.get("", {
					params: {
						action: "GetUploadHistory",
						house_id: this.house_id, // ← this.houseId との表記ゆれに注意
					},
				});

				// 取得データをストアへ
				const uploadHistory = data ?? {};
				blobStore.setUploadHistory(uploadHistory);

				const directoryGroupedHistory = blobStore.uploadHistory ?? {};

				// items が未初期化なら配列に
				if (!Array.isArray(this.items)) this.items = [];

				Object.entries(directoryGroupedHistory).forEach(([directory, histories]) => {
					const arr = Array.isArray(histories) ? histories : [];
					if (arr.length === 0) {
						// 該当ディレクトリの row があるなら「-」を入れて明示したい場合はここで処理
						const target = this.items.find((i) => i.path === directory);
						if (target) {
							if (this.$set) {
								this.$set(target, "user", "-");
								this.$set(target, "date", "-");
							} else {
								target.user = "-";
								target.date = "-";
							}
						}
						return;
					}

					// 「最新」判定（サーバ側が降順で返していない前提で厳密化）
					const latestUpload = arr.reduce((acc, cur) => {
						const ad = new Date(acc?.upload_date ?? 0);
						const cd = new Date(cur?.upload_date ?? 0);
						return cd > ad ? cur : acc;
					}, arr[0]);

					const userName = latestUpload?.user_name ?? "-";
					const upDate = latestUpload?.upload_date ?? "-";

					const idx = this.items.findIndex((item) => item.path === directory);
					if (idx !== -1) {
						if (this.$set) {
							this.$set(this.items[idx], "user", userName);
							this.$set(this.items[idx], "date", upDate);
						} else {
							this.items[idx].user = userName;
							this.items[idx].date = upDate;
						}
					} else {
						// 行がまだ無ければ追加（必要がなければこのブロックは削除）
						this.items.push({
							path: directory,
							user: userName,
							date: upDate,
						});
					}
				});
			} catch (error) {
				console.error("Error fetching upload history:", error);
				this.errorMessage = "アップロード履歴の取得に失敗しました。";
			}
		},

		// Blobストレージからファイルをダウンロード
		async handleFileDownload(fileLink) {
			this.isLoading = true; // ローディング開始

			try {
				await downloadFile(fileLink, this.accessToken); // 共通メソッドを呼び出す
			} catch (error) {
				console.error(error.message); // エラーメッセージを設定
			} finally {
				this.isLoading = false; // ローディング終了
			}
		},

		// データ更新モーダルをオープン
		openUploadModal(itemName) {
			this.modalTitle = itemName; // モーダルのタイトルを設定
			this.isModalVisible = true; // モーダルを表示
		},

		// データ更新モーダルをクローズ
		closeUploadModal() {
			this.isModalVisible = false; // モーダルを非表示
		},

		// アップロード成功時のメッセージ表示
		handleUploadSuccess(fileName) {
			this.successMessage = `${fileName} のアップロードが成功しました。`; // 成功メッセージ設定
			this.errorMessage = ""; // 失敗メッセージをクリア
			// this.checkAccount(); // アカウント情報のチェック
		},

		// アップロード失敗時のメッセージ表示
		handleUploadFail(fileName) {
			this.errorMessage = `${fileName} のアップロードに失敗しました。`; // 失敗メッセージ設定
			this.successMessage = ""; // 成功メッセージをクリア
		},

		// DB登録成功時のメッセージ表示
		handleDBSuccess(fileName) {
			console.log(`${fileName} のDB登録が成功しました。`); // DB登録成功時のメッセージ
			// this.successMessage += `DBへの登録も成功しました。`; // 追加の成功メッセージ
			this.checkAccount(); // アカウント情報のチェック
		},
		// DB登録失敗時のメッセージ表示
		handleDBFail(fileName) {
			this.errorMessage = `${fileName} のDB登録に失敗しました。`; // DB登録失敗時のメッセージ
			console.error(`DB登録失敗：${fileName}`); // 失敗をコンソールに出力
		},
	},
};
</script>

<style scoped>
/* 画面全体 */
html,
body {
	padding: 0;
	margin: 0;
	background-color: white;
}

/* タイトルのスタイル */
.data-update-title {
	color: black; /* フォントカラー */
	background-color: rgb(136, 174, 82); /* タイトルの背景色 */
	padding: 15px;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

/* コンテンツのスタイル */
.data-update-content {
	margin: 20px; /* タイトルとコンテンツの間の余白 */
}

/* 成功メッセージのスタイル */
.success-message {
	color: white; /* メッセージの色 */
	font-size: 16px; /* フォントサイズ */
	padding: 10px; /* メッセージのパディング */
	text-align: center; /* テキストを中央揃え */
	background-color: rgb(12, 146, 236); /* メッセージの背景色 */
}

/* エラーメッセージのスタイル */
.error-message {
	color: white; /* メッセージの色 */
	font-size: 16px; /* フォントサイズ */
	padding: 10px; /* メッセージのパディング */
	text-align: center; /* テキストを中央揃え */
	background-color: red; /* メッセージの背景色 */
}

/* クローズボタンのスタイル */
.close-button {
	position: absolute; /* 絶対位置で配置 */
	top: 75px;
	right: 10px; /* メッセージの右上に配置 */
	cursor: pointer; /* カーソルをポインターに */
	font-weight: bold; /* 太字 */
}

/* テーブルスタイル */
table {
	width: 100%; /* テーブルが親要素の幅いっぱいに */
	border-collapse: collapse; /* ボーダーを重ねる */
}

th,
td {
	border: 1px solid #dddddd; /* ボーダーを設定 */
	text-align: left; /* テキストを左揃え */
	padding: 8px; /* パディング */
	background-color: white; /* ヘッダーの背景色 */
}

th {
	background-color: rgb(15, 74, 104); /* ヘッダーの背景色 */
	color: white; /* ヘッダーのテキスト色 */
}

ul {
	list-style-type: none; /* リストマーカーを無効にする */
	padding: 0; /* パディングをリセット */
	margin: 0; /* マージンをリセット */
}

/* ローディングオーバーレイのスタイル */
.loading-overlay {
	position: fixed; /* 固定位置で表示 */
	top: 0; /* 上端を画面の0に設定 */
	left: 0; /* 左端を画面の0に設定 */
	right: 0; /* 右端を画面の100%に設定 */
	bottom: 0; /* 下端を画面の0に設定 */
	background: rgba(128, 128, 128, 0.5); /* グレーの背景、透明度50%でオーバーレイ表示 */
	display: flex; /* フレックスボックスで配置 */
	justify-content: center; /* コンテンツを中央に配置 */
	align-items: center; /* コンテンツを垂直方向に中央に配置 */
	z-index: 1000; /* 他の要素より前に表示 */
}

/* ローディングスピナーのスタイル */
.loading-spinner {
	width: 30px; /* スピナーのサイズ（幅） */
	height: 30px; /* スピナーのサイズ（高さ） */
	border: 4px solid rgba(0, 0, 0, 0.1); /* スピナーの外周の色 */
	border-top-color: #000; /* スピナーの回転部分の色 */
	border-radius: 50%; /* スピナーを円形にする */
	animation: spin 1s linear infinite; /* スピナーに回転アニメーションを設定 */
	margin: 10px auto; /* 中央寄せ */
}

@keyframes spin {
	to {
		transform: rotate(360deg); /* 360度回転するアニメーション */
	}
}
</style>
